import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContactlessIcon from "@mui/icons-material/Contactless";
import { useTranslation } from "react-i18next";
import {
  cancelarSolicitudTpv,
  getSolicitudTpvEstado,
  getTipById,
  solicitarTpv,
} from "../api/tip";
import { getBusinessById } from "../api/business";
import { calcularTasaFinal } from "../utils/helpers";
import {
  clearTipFormSession,
  isTipAlreadyPaidError,
  isTipPaid,
} from "../utils/tipPaymentStatus";
import BusinessBrandingHeader, {
  getBusinessBrandPrimary,
} from "../components/BusinessBrandingHeader";
import logo from "../assets/logo.png";
import "./StripeScreen.css";

/** Cada cuánto consulta la web si el camarero ya ha cobrado. */
const POLL_MS = 3000;

/**
 * "Pagar con el datáfono Tipify": el cliente ya ha elegido la propina; aquí
 * avisamos a la app TPV del local y esperamos a que el camarero la cobre con
 * Tap to Pay. Cuando la propina queda cobrada, vamos a la pantalla de éxito.
 */
function TpvWaitScreen() {
  const { id: businessId, idtip: tipId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Dónde está el cliente (opcional), escrito junto al botón del datáfono.
  // Se reenvía al volver a avisar para que el camarero lo siga viendo.
  const ubicacion =
    typeof location.state?.tpvUbicacion === "string"
      ? location.state.tpvUbicacion
      : null;

  const [tip, setTip] = useState(null);
  const [business, setBusiness] = useState(null);
  // loading | pending | attending | cancelledByTpv | expired | unavailable | error
  const [phase, setPhase] = useState("loading");
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef(null);
  const leavingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const goToSuccess = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    stopPolling();
    clearTipFormSession();
    navigate(`/${businessId}/success/${tipId}?method=stripe`, { replace: true });
  }, [businessId, navigate, stopPolling, tipId]);

  const goToCardPayment = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    stopPolling();
    const method = business?.paymentProvider || "stripe";
    navigate(`/${businessId}/payout/${tipId}?method=${method}`, { replace: true });
  }, [business?.paymentProvider, businessId, navigate, stopPolling, tipId]);

  const applyStatus = useCallback(
    (status) => {
      if (!status) return;
      if (status.tipPaid) {
        goToSuccess();
        return;
      }
      switch (status.estado) {
        case "pending":
          setPhase("pending");
          break;
        case "attending":
          setPhase("attending");
          break;
        case "done":
          goToSuccess();
          break;
        case "cancelled":
          stopPolling();
          if (status.cancelledBy === "web") {
            // Cancelada desde otra pestaña del propio cliente
            goToCardPayment();
          } else {
            setPhase("cancelledByTpv");
          }
          break;
        case "expired":
          stopPolling();
          setPhase("expired");
          break;
        default:
          break;
      }
    },
    [goToCardPayment, goToSuccess, stopPolling],
  );

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await getSolicitudTpvEstado(tipId);
        applyStatus(res.data);
      } catch (error) {
        if (isTipAlreadyPaidError(error)) {
          goToSuccess();
          return;
        }
        // Sin red momentánea: seguimos intentando.
        console.warn("No se pudo consultar el estado de la solicitud TPV:", error);
      }
    }, POLL_MS);
  }, [applyStatus, goToSuccess, stopPolling, tipId]);

  const requestTpv = useCallback(async () => {
    setPhase("loading");
    try {
      const res = await solicitarTpv(tipId, { ubicacion });
      applyStatus(res.data);
      startPolling();
    } catch (error) {
      if (isTipAlreadyPaidError(error)) {
        goToSuccess();
        return;
      }
      const code = error?.response?.data?.code;
      if (code === "TPV_LLAMADA_DISABLED") {
        setPhase("unavailable");
        return;
      }
      console.error("Error al solicitar el datáfono:", error);
      setPhase("error");
    }
  }, [applyStatus, goToSuccess, startPolling, tipId, ubicacion]);

  // Fondo blanco como en la pantalla de pago
  useEffect(() => {
    const original = {
      backgroundColor: document.body.style.backgroundColor,
      background: document.body.style.background,
    };
    document.body.style.backgroundColor = "#ffffff";
    document.body.style.background = "#ffffff";
    return () => {
      document.body.style.backgroundColor = original.backgroundColor;
      document.body.style.background = original.background;
    };
  }, []);

  // Carga de propina y negocio; después avisa al TPV
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const tipRes = await getTipById(tipId);
        const tipData = tipRes?.data;
        if (!tipData) throw new Error("No se pudo obtener la propina.");
        if (isTipPaid(tipData.estadoPago)) {
          goToSuccess();
          return;
        }
        if (cancelled) return;
        setTip(tipData);

        const bizId =
          typeof tipData.business === "string"
            ? tipData.business
            : tipData.business?._id;
        if (bizId) {
          try {
            const bizRes = await getBusinessById(bizId);
            if (!cancelled && bizRes?.data) setBusiness(bizRes.data);
          } catch (bizErr) {
            console.warn("No se pudo cargar el negocio:", bizErr);
          }
        }
        if (!cancelled) await requestTpv();
      } catch (error) {
        if (isTipAlreadyPaidError(error)) {
          goToSuccess();
          return;
        }
        console.error("Error preparando la solicitud TPV:", error);
        if (!cancelled) setPhase("error");
      }
    };

    load();
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipId]);

  const handleCancel = async () => {
    setCancelling(true);
    stopPolling();
    try {
      await cancelarSolicitudTpv(tipId);
    } catch (error) {
      console.warn("No se pudo cancelar la solicitud TPV:", error);
    } finally {
      setCancelling(false);
      goToCardPayment();
    }
  };

  const brandPrimary = getBusinessBrandPrimary(business);
  const importe = tip?.importe?.import || 0;
  const total = tip?.clickTaxes
    ? importe + calcularTasaFinal(importe, tip?.tasa)
    : importe;
  const amountLabel = total.toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
  });

  if (phase === "loading" && !tip) {
    return (
      <div className="loading-container">
        <img src={logo} alt="Tipify Logo" className="loading-logo pulse-effect" />
      </div>
    );
  }

  const waiting = phase === "loading" || phase === "pending" || phase === "attending";

  const renderStatus = () => {
    switch (phase) {
      case "attending":
        return {
          title: t("tpvCall.attendingTitle"),
          subtitle: t("tpvCall.attendingSubtitle"),
        };
      case "cancelledByTpv":
        return {
          title: t("tpvCall.cancelledTitle"),
          subtitle: t("tpvCall.cancelledSubtitle"),
        };
      case "expired":
        return {
          title: t("tpvCall.expiredTitle"),
          subtitle: t("tpvCall.expiredSubtitle"),
        };
      case "unavailable":
        return { title: t("common.oops"), subtitle: t("tpvCall.unavailable") };
      case "error":
        return { title: t("common.oops"), subtitle: t("tpvCall.error") };
      case "loading":
      case "pending":
      default:
        return {
          title: t("tpvCall.waitingTitle"),
          subtitle: t("tpvCall.waitingSubtitle"),
        };
    }
  };

  const status = renderStatus();

  return (
    <Box
      component="main"
      sx={{ minHeight: "100vh", bgcolor: "#ffffff", width: "100%", m: 0, p: 0 }}
    >
      <Box
        sx={{
          bgcolor: "#ffffff",
          p: { xs: 3, sm: 4, md: 5 },
          width: "100%",
          minHeight: "100vh",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <IconButton
          onClick={handleCancel}
          disabled={cancelling}
          sx={{
            position: "absolute",
            left: { xs: 16, sm: 12 },
            top: { xs: 16, sm: 12 },
            color: brandPrimary,
            zIndex: 10,
            "&:hover": { bgcolor: `${brandPrimary}1A` },
          }}
          aria-label={t("tpvCall.back")}
        >
          <ArrowBackIcon /> {t("tpvCall.back")}
        </IconButton>

        <Box sx={{ mb: 2, mt: { xs: 7, sm: 5 } }}>
          <BusinessBrandingHeader business={business} />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 2,
            py: 4,
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              border: `3px solid ${brandPrimary}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {waiting && (
              <CircularProgress
                size={120}
                thickness={1.5}
                sx={{ color: brandPrimary, position: "absolute", top: -3, left: -3 }}
              />
            )}
            <ContactlessIcon
              className={waiting ? "pulse-effect" : undefined}
              sx={{ fontSize: 64, color: brandPrimary }}
            />
          </Box>

          <Typography variant="h5" fontWeight={600} sx={{ mt: 1 }}>
            {status.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360 }}>
            {status.subtitle}
          </Typography>
          {tip && (
            <Typography variant="h6" fontWeight={500} sx={{ color: brandPrimary }}>
              {t("tpvCall.amount", { amount: amountLabel })}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 2 }}>
          {waiting ? (
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleCancel}
              disabled={cancelling}
              sx={{
                textTransform: "none",
                borderRadius: "30px",
                color: brandPrimary,
                borderColor: brandPrimary,
              }}
            >
              {cancelling ? t("common.loading") : t("tpvCall.cancel")}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={goToCardPayment}
                sx={{
                  textTransform: "none",
                  borderRadius: "30px",
                  bgcolor: brandPrimary,
                  "&:hover": { bgcolor: brandPrimary, filter: "brightness(0.92)" },
                }}
              >
                {t("tpvCall.payByCard")}
              </Button>
              {phase !== "unavailable" && (
                <Button
                  variant="text"
                  fullWidth
                  onClick={requestTpv}
                  sx={{ textTransform: "none", color: brandPrimary }}
                >
                  {t("tpvCall.retry")}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default TpvWaitScreen;
